import type { PatientView } from "@omony/domain";
import { Button } from "@omony/ui";
import { useMutation } from "@tanstack/react-query";
import { BellRing, CheckCircle2, LockKeyhole } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { api } from "../../shared/api.js";
import styles from "../console.module.css";
import { PatientLanguage } from "../PatientLanguage.js";
import { VideoSlot } from "../../shared/video.js";

/**
 * Patientenmodus (ARCHITEKTUR §10.5): Gerät gesperrt, Gespräch läuft. Video bleibt eingebettet,
 * dazu Hinweise in Patientensprache und „Personal rufen“. Keine Personal- oder Stammdaten.
 */
export function PatientMode({
  view,
  ended,
  onUnlock,
}: {
  /** laufender Vorgang des Geräts; `null`, wenn das Gespräch gerade beendet wurde */
  view: PatientView | null;
  ended: { patientLanguage: string } | null;
  onUnlock: () => void;
}) {
  const staff = useIntl();
  const language = view?.patientLanguage ?? ended?.patientLanguage ?? "de";
  return (
    <main id="main" className={styles.patientMode}>
      <PatientLanguage locale={language} badge={false} className={styles.patientModeInner}>
        {view && !ended ? <Live view={view} /> : <Ended />}
      </PatientLanguage>
      <Button variant="quiet" className={styles.staffUnlock} icon={<LockKeyhole />} onPress={onUnlock}>
        {staff.formatMessage({ id: "console.patientMode.unlock" })}
      </Button>
    </main>
  );
}

function Live({ view }: { view: PatientView }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const [called, setCalled] = useState(false);
  const call = useMutation({
    mutationFn: () => api("/device/staff-call", { method: "POST" }),
    onSuccess: () => setCalled(true),
  });
  return (
    <div className={styles.patientLive}>
      <div className={styles.patientVideo}>
        <VideoSlot caseId={view.caseId} className={styles.videoSlotFill} />
      </div>
      <aside className={styles.patientSide}>
        <h1 className={styles.patientTitle}>
          {view.status === "in_behandlung" && view.doctorName
            ? t("patient.mode.inCall", { name: view.doctorName })
            : t("patient.mode.connecting")}
        </h1>
        <p className={styles.patientText}>{t("patient.mode.hint")}</p>
        {view.vitalsRequested ? (
          <p className={styles.patientNotice} role="status">
            {t("patient.mode.vitals")}
          </p>
        ) : null}
        <div role="status" aria-live="polite">
          {called ? <p className={styles.patientNotice}>{t("patient.mode.staffCalled")}</p> : null}
        </div>
        <Button
          size="lg"
          variant="secondary"
          icon={<BellRing />}
          onPress={() => call.mutate()}
          isDisabled={call.isPending}
        >
          {t("patient.mode.staffCall")}
        </Button>
      </aside>
    </div>
  );
}

function Ended() {
  const intl = useIntl();
  return (
    <div className={styles.patientEnded} role="status">
      <CheckCircle2 aria-hidden="true" className={styles.patientEndedIcon} />
      <h1 className={styles.patientTitle}>{intl.formatMessage({ id: "patient.mode.endedTitle" })}</h1>
      <p className={styles.patientText}>{intl.formatMessage({ id: "patient.mode.ended" })}</p>
    </div>
  );
}
